import { useMemo, useState } from "react";
import { Flag } from "lucide-react";
import { money } from "../../lib/api";
import { Panel, Result, Cta, Segmented, ApplySize, useTool, field, label, fmt } from "./_ui";

const MATERIALS = [
  { key: "vinyl13", name: "13oz Matte Vinyl", slug: "banner-vinyl-outdoor", rate: 2.9, oz: 13, outdoor: true, wind: "ok", sides: 1 },
  { key: "block18", name: "18oz Blockout Vinyl", slug: "18oz-blockout-banner", rate: 4.5, oz: 18, outdoor: true, wind: "good", sides: 2 },
  { key: "smooth", name: "Indoor Super Smooth", slug: "indoor-super-smooth-banner", rate: 2.82, oz: 12, outdoor: false, wind: null, sides: 1 },
  { key: "mesh", name: "10oz Mesh 70/30", slug: "fabric-banner-wrinckle-free", rate: 2.98, oz: 10, outdoor: true, wind: "best", sides: 2 },
  { key: "fabric9", name: "9oz Wrinkle-Free Fabric", slug: "9oz-wrinkle-free-fabric-banner", rate: 3.85, oz: 9, outdoor: false, wind: null, sides: 1 },
  { key: "tension", name: "8.8oz Tension Fabric", slug: "tension-fabric-banners", rate: 5.85, oz: 8.8, outdoor: false, wind: null, sides: 2 },
];

export function BannerCalculator() {
  const { onApplySize } = useTool();
  const [wFt, setWFt] = useState(6);
  const [hFt, setHFt] = useState(3);
  const [distance, setDistance] = useState(50);
  const [place, setPlace] = useState("outdoor");
  const [wind, setWind] = useState("sheltered");
  const [sides, setSides] = useState(1);

  const r = useMemo(() => {
    const sqft = wFt * hFt;
    const perim = 2 * (wFt + hFt);
    const grommets = Math.max(4, Math.ceil(perim / 2));
    const letterIn = Math.max(1, Math.ceil(distance / 10));
    const headlineIn = Math.max(2, Math.ceil(distance / 6));
    const rec = MATERIALS.filter((m) => (place === "outdoor" ? m.outdoor : true))
      .filter((m) => (place === "outdoor" && wind === "open" ? m.wind === "best" || m.wind === "good" : true))
      .filter((m) => (sides === 2 ? m.sides === 2 : true))
      .sort((a, b) => (place === "outdoor" && wind === "open" ? (a.wind === "best" ? -1 : 1) : a.rate - b.rate));
    return { sqft, perim, grommets, letterIn, headlineIn, rec, weightLb: (m) => (sqft / 9) * m.oz / 16 };
  }, [wFt, hFt, distance, place, wind, sides]);

  return (
    <Panel icon={Flag} title="Banner size, readability & material calculator" testId="banner-calculator">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div><label className={label}>Width (ft)</label><input type="number" step="0.5" min="1" value={wFt} onChange={(e) => setWFt(+e.target.value)} className={field} data-testid="banner-width" /></div>
        <div><label className={label}>Height (ft)</label><input type="number" step="0.5" min="1" value={hFt} onChange={(e) => setHFt(+e.target.value)} className={field} data-testid="banner-height" /></div>
        <div><label className={label}>Viewing distance (ft)</label><input type="number" step="5" min="5" value={distance} onChange={(e) => setDistance(+e.target.value)} className={field} data-testid="banner-distance" /></div>
        <div><label className={label}>Where</label><Segmented value={place} onChange={setPlace} options={[{ value: "outdoor", label: "Outdoor" }, { value: "indoor", label: "Indoor" }]} testId="banner-place" /></div>
        <div><label className={label}>Wind exposure</label><Segmented value={wind} onChange={setWind} options={[{ value: "sheltered", label: "Wall / sheltered" }, { value: "open", label: "Fence / open" }]} testId="banner-wind" /></div>
        <div><label className={label}>Sides</label><Segmented value={sides} onChange={setSides} options={[{ value: 1, label: "1" }, { value: 2, label: "2" }]} testId="banner-sides" /></div>
      </div>
      <div className="grid sm:grid-cols-4 gap-3 mt-5">
        <Result title="Area" value={`${fmt(r.sqft)} sq ft`} sub={`${wFt * 12}" × ${hFt * 12}" — add 0.25" bleed each side`} testId="banner-result-area" />
        <Result title="Grommets" value={`${r.grommets}`} sub={`every 2 ft around ${fmt(r.perim)} ft of hem`} testId="banner-result-grommets" />
        <Result title="Min. body text" value={`${r.letterIn}" tall`} sub={`readable at ${distance} ft (1" per 10 ft)`} testId="banner-result-letter" />
        <Result title="Headline" value={`${r.headlineIn}" tall`} sub="grabs attention at that distance" testId="banner-result-headline" />
      </div>
      <div className="mt-5">
        <div className={label}>Recommended materials for this job</div>
        <div className="divide-y divide-white/10 border border-white/10" data-testid="banner-recommendations">
          {r.rec.map((m, i) => (
            <div key={m.key} className="grid grid-cols-12 items-center gap-2 px-3 py-2 text-sm">
              <div className="col-span-5 sm:col-span-4 font-bold">{i === 0 && <span className="font-mono text-[9px] tracking-widest text-[#E8B587] mr-2">BEST</span>}{m.name}</div>
              <div className="col-span-3 sm:col-span-2 font-mono text-[#E8B587]">{money(r.sqft * m.rate)}</div>
              <div className="hidden sm:block col-span-3 text-xs text-white/60">≈ {fmt(r.weightLb(m), 1)} lb · {m.wind === "best" ? "30% wind passes through" : m.wind === "good" ? "heavy, tear-resistant" : m.outdoor ? "3–5 yrs outdoors" : "indoor only"}</div>
              <div className="col-span-4 sm:col-span-3 text-right">
                <ApplySize w={wFt * 12} h={hFt * 12} productSlug={m.slug} testId={`banner-apply-${m.key}`}>{onApplySize ? "Use size" : "Configure"}</ApplySize>
              </div>
            </div>
          ))}
          {r.rec.length === 0 && <div className="px-3 py-3 text-sm text-white/70">No single material fits every filter — ask us about a custom build.</div>}
        </div>
        <p className="text-[11px] text-white/50 mt-2">Prices are material-only estimates from our per-sq-ft rates before finishing options and shipping; the product page gives the exact figure.</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Cta to="/blog/which-banner-material-vinyl-blockout-mesh-fabric-compared" outline testId="banner-cta-guide">Compare all six banners</Cta>
        <Cta to="/quote?rush=1" testId="banner-cta-rush">Rush job? Get a quote</Cta>
      </div>
    </Panel>
  );
}
